'use client'

import {useContext, useEffect, useRef, useState, type ReactNode} from 'react'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import Image from 'next/image'
import Link from 'next/link'
import {Check, ChevronDown, LogOut, Menu, Music2, User, X} from 'lucide-react'
import {useTranslation} from '../../../hooks/useTranslation'
import {usePartnerStaffAccess} from '../../../hooks/usePartnerStaffAccess'
import {useScrollLock} from '../../../hooks/useScrollLock'
import {useAuth, useClerk, useUser} from '@fitro/customer-host/auth'
import {canAccessAdminDashboard} from '../../../lib/staff-access'
import {clearExpiredGuestContactDraft} from '../../../lib/guest-contact-draft'
import {breakpoints, cn} from '../../../lib/utils'
import type {LocaleCode} from '../../../types/i18n/core'
import {publicSans} from '../../../lib/public-font'
import {InstagramIcon} from '../../ui/icons'
import {buildAuthHref} from './publicEntryLinks'
import {publicEntryStyles} from './publicEntryStyles'
import {PUBLIC_SITE_NAV_ITEMS, type PublicSitePage} from './publicSiteNavigation'
import {HostTopBanner} from '@fitro/customer-host/chrome'
import {POPOVER_MOTION_CLASS} from '@fitro/customer-ui/popoverMotion'
import {useRetainedMotionState} from '@fitro/customer-ui/useRetainedMotionState'

type PublicSiteShellProps = {
    activePage: PublicSitePage
    children: ReactNode
    footerSocialLinks?: boolean
    /** The fitro.pl page for studios like this one, linked from the footer. */
    fitroUrl?: string
    partnerName: string
    redirectUrl?: string
    showAboutPage?: boolean
}

type PublicAuthActionsProps = {
    compact?: boolean
    redirectUrl?: string
}

const AUTH_REDIRECT_URL = '/home'
export const PUBLIC_SITE_CONTENT_CLASS_NAME = publicEntryStyles.content

export function PublicSiteShell(props: PublicSiteShellProps) {
    const inherited = useContext(PublicEntryChromeContext)
    useEffect(() => inherited && props.redirectUrl ? inherited.registerRedirect(props.redirectUrl) : undefined,
        [inherited, props.redirectUrl])
    return inherited ? props.children : <StandalonePublicSiteShell {...props}/>
}

function StandalonePublicSiteShell({
    activePage,
    children,
    footerSocialLinks = false,
    fitroUrl = 'https://fitro.pl/',
    partnerName,
    redirectUrl,
    showAboutPage = true,
}: PublicSiteShellProps) {
    const {t} = useTranslation('landing')
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const mobileMenuTriggerRef = useRef<HTMLButtonElement | null>(null)

    useScrollLock({enabled: mobileMenuOpen})
    const mobileMenuMotion = useRetainedMotionState(mobileMenuOpen)
    const isMobileMenuClosing = !mobileMenuOpen

    const closeMobileMenu = () => {
        setMobileMenuOpen(false)
        mobileMenuTriggerRef.current?.focus()
    }

    useEffect(clearExpiredGuestContactDraft, [])

    useEffect(() => {
        const closeMenuAtDesktopBreakpoint = () => {
            if (window.innerWidth >= breakpoints.lg) setMobileMenuOpen(false)
        }

        window.addEventListener('resize', closeMenuAtDesktopBreakpoint)
        closeMenuAtDesktopBreakpoint()
        return () => window.removeEventListener('resize', closeMenuAtDesktopBreakpoint)
    }, [])

    useEffect(() => {
        if (!mobileMenuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.key !== 'Escape') return
            closeMobileMenu()
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [mobileMenuOpen])

    return (
        <main
            className={cn(publicSans.variable, publicEntryStyles.shellRoot)}
            style={{
                colorScheme: 'light',
            }}
        >
            <HostTopBanner activeView="client-public" />
            <div className={publicEntryStyles.shellBody}>
                <header className={publicEntryStyles.header}>
                    <div className={cn(PUBLIC_SITE_CONTENT_CLASS_NAME, publicEntryStyles.headerInner)}>
                        <div className="flex min-w-0 items-center gap-7">
                            <Link href="/" onClick={closeMobileMenu} className="truncate rounded-sm text-[18px] font-semibold text-[var(--landing-primary-600)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-focus-ring)] focus-visible:ring-offset-2 md:text-[20px]">
                                {partnerName.trim() || t('publicEntry.fallbackStudioName')}
                            </Link>
                        </div>

                        <PublicNavigation activePage={activePage} className="hidden lg:flex" showAboutPage={showAboutPage} />

                        <div className="hidden items-center gap-3 lg:flex">
                            <PublicAuthActions redirectUrl={redirectUrl} />
                            <PublicLanguageSelector />
                        </div>

                        <button
                            ref={mobileMenuTriggerRef}
                            className="flex h-10 w-10 items-center justify-center rounded-md text-[var(--landing-neutral-900)] lg:hidden"
                            type="button"
                            aria-expanded={mobileMenuOpen}
                            aria-label={t('publicEntry.nav.menu')}
                            onClick={() => setMobileMenuOpen((open) => !open)}
                        >
                            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
                        </button>
                    </div>

                </header>

                {mobileMenuMotion.shouldRender ? (
                    <>
                        <button
                            type="button"
                            aria-label={t('publicEntry.nav.closeMenu')}
                            tabIndex={-1}
                            data-state={mobileMenuMotion.motionState}
                            inert={isMobileMenuClosing || undefined}
                            className={cn(
                                'fixed inset-x-0 bottom-0 top-[calc(4rem+env(safe-area-inset-top))] z-30 bg-black/25 lg:hidden',
                                'transition-opacity duration-normal ease-out',
                                'data-[state=open]:opacity-100 data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0',
                            )}
                            onClick={closeMobileMenu}
                            data-testid="public-mobile-menu-backdrop"
                        />
                        <div
                            data-state={mobileMenuMotion.motionState}
                            aria-hidden={isMobileMenuClosing || undefined}
                            inert={isMobileMenuClosing || undefined}
                            className={cn(
                                'ios-momentum-scroll fixed inset-x-0 top-[calc(4rem+env(safe-area-inset-top))] z-40 max-h-[calc(100svh-4rem-env(safe-area-inset-top))] overflow-y-auto overscroll-contain border-b border-slate-100 bg-white pl-[calc(1.25rem+env(safe-area-inset-left,0px))] pr-[calc(1.25rem+env(safe-area-inset-right,0px))] py-3 shadow-2xl lg:hidden',
                                'transition-[opacity,translate] duration-normal ease-out',
                                'data-[state=open]:translate-y-0 data-[state=open]:opacity-100',
                                'data-[state=closed]:pointer-events-none data-[state=closed]:-translate-y-3 data-[state=closed]:opacity-0',
                                'motion-reduce:data-[state=closed]:translate-y-0',
                            )}
                            data-testid="public-mobile-menu"
                        >
                            <PublicNavigation activePage={activePage} className="grid gap-1" compact showAboutPage={showAboutPage} onNavigate={closeMobileMenu} />
                            <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                                <PublicAuthActions compact redirectUrl={redirectUrl} />
                                <PublicLanguageSelector compact />
                            </div>
                        </div>
                    </>
                ) : null}

                {children}

                <footer className={publicEntryStyles.footer}>
                    <div className={cn(PUBLIC_SITE_CONTENT_CLASS_NAME, publicEntryStyles.footerInner)}>
                        <div className="flex flex-col items-start gap-1.5">
                            <p>{t('publicEntry.footer.copyright', {partnerName})}</p>
                            <div className="inline-flex items-center gap-2 text-[12px] text-[var(--landing-neutral-400)]">
                                <span>{t('publicEntry.footer.poweredBy')}</span>
                                <a href={`${fitroUrl}?utm_source=studio&utm_medium=powered_by&utm_campaign=studio_site`} target="_blank" rel="noreferrer" aria-label="Fitro">
                                    <Image src="/landing/logo.svg" alt="Fitro" width={58} height={16} className="h-4 w-auto opacity-70" />
                                </a>
                            </div>
                        </div>
                        <div className={publicEntryStyles.footerLinks}>
                            <Link href="/cennik">{t('publicEntry.nav.pricing')}</Link>
                            <Link href="/regulamin">{t('publicEntry.nav.terms')}</Link>
                            <Link href="/polityka-prywatnosci">{t('publicEntry.nav.privacy')}</Link>
                            {footerSocialLinks ? (
                                <>
                                    <a href="#instagram" aria-label="Instagram">
                                        <InstagramIcon className="h-5 w-5" />
                                    </a>
                                    <a href="#facebook" aria-label="Facebook" className="text-base font-bold">
                                        f
                                    </a>
                                    <a href="#tiktok" aria-label="TikTok">
                                        <Music2 className="h-5 w-5" aria-hidden="true" />
                                    </a>
                                </>
                            ) : null}
                        </div>
                    </div>
                </footer>
            </div>
        </main>
    )
}

function PublicLanguageSelector({compact = false}: {compact?: boolean}) {
    const {changeLocale, locale, t} = useTranslation('landing')
    const [open, setOpen] = useState(false)
    const {shouldRender, motionState} = useRetainedMotionState(open, 150)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const options: Array<{code: LocaleCode; labelKey: string}> = [
        {code: 'pl', labelKey: 'publicEntry.nav.languagePolish'},
        {code: 'en', labelKey: 'publicEntry.nav.languageEnglish'},
    ]
    const activeOption = options.find((option) => option.code === locale) ?? options[0]

    useEffect(() => {
        if (!open) return

        const handlePointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false)
            }
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                setOpen(false)
            }
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    const switchLocale = (code: LocaleCode) => {
        setOpen(false)
        if (code !== locale) {
            void changeLocale(code)
        }
    }

    return (
        <div
            ref={rootRef}
            className={cn(
                'relative',
                compact ? 'inline-flex text-[14px] leading-none' : 'inline-flex text-base leading-none'
            )}
            aria-label={t('publicEntry.nav.language')}
        >
            <button
                type="button"
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label={`${t('publicEntry.nav.language')}: ${activeOption.code.toUpperCase()}`}
                className={cn(
                    compact
                        ? 'inline-flex h-9 min-w-[74px] items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-3 text-[14px] font-medium text-[var(--landing-primary-600)] transition-colors hover:border-[var(--landing-primary-300)]'
                        : 'inline-flex h-9 min-w-[66px] items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-3 text-[14px] font-medium text-[var(--landing-primary-600)] shadow-[0_4px_12px_rgba(15,23,42,0.04)] transition-colors hover:border-[var(--landing-primary-300)]',
                )}
                onClick={() => setOpen((value) => !value)}
            >
                <span>{activeOption.code.toUpperCase()}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform duration-normal ease-out motion-reduce:transition-none', open && 'rotate-180')} aria-hidden="true" />
            </button>

            {shouldRender ? (
                <div
                    data-state={motionState}
                    aria-hidden={!open || undefined}
                    inert={!open || undefined}
                    className={cn(
                        'rounded-lg border border-slate-200 bg-white p-1 shadow-[0_14px_34px_rgba(15,23,42,0.12)]',
                        POPOVER_MOTION_CLASS,
                        // The compact picker sits last in the scrolling mobile menu, which clips anything below it, so it opens upwards.
                        compact
                            ? 'absolute bottom-[calc(100%+8px)] left-0 z-30 w-40 origin-bottom-left'
                            : 'absolute right-0 top-[calc(100%+8px)] z-30 w-40 origin-top-right',
                    )}
                    role="menu"
                    aria-label={t('publicEntry.nav.language')}
                >
                    {options.map((option) => (
                        <button
                            key={option.code}
                            type="button"
                            role="menuitemradio"
                            aria-checked={locale === option.code}
                            className={cn(
                                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[14px] leading-none transition-colors',
                                locale === option.code
                                    ? 'bg-[rgba(237,240,253,0.88)] font-medium text-[var(--landing-primary-600)]'
                                    : 'text-[var(--landing-neutral-600)] hover:bg-slate-50 hover:text-[var(--landing-primary-600)]',
                            )}
                            onClick={() => switchLocale(option.code)}
                        >
                            <span>{t(option.labelKey)}</span>
                            {locale === option.code ? <Check className="h-4 w-4" aria-hidden="true" /> : <span className="h-4 w-4" aria-hidden="true" />}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    )
}

type PublicPageHeroProps = {
    actions?: ReactNode
    eyebrow?: ReactNode
    subtitle?: ReactNode
    titleId?: string
    title: ReactNode
}

export function PublicPageHero({actions, eyebrow, subtitle, title, titleId}: PublicPageHeroProps) {
    return (
        <section data-testid="public-page-hero" className={publicEntryStyles.pageHeader}>
            <div className={publicEntryStyles.pageHeaderRow}>
                <div className="min-w-0">
                    {eyebrow ? (
                        <p className={publicEntryStyles.heroEyebrow}>{eyebrow}</p>
                    ) : null}
                    <h1 id={titleId} data-testid="public-page-hero-title" className={cn(eyebrow && 'mt-3', publicEntryStyles.heroTitle)}>
                        {title}
                    </h1>
                    {subtitle ? (
                        <p className={publicEntryStyles.heroSubtitle}>{subtitle}</p>
                    ) : null}
                </div>
                {actions ? (
                    <div className={publicEntryStyles.heroActions}>{actions}</div>
                ) : null}
            </div>
        </section>
    )
}

function PublicAuthActions({compact = false, redirectUrl = AUTH_REDIRECT_URL}: PublicAuthActionsProps) {
    const {t} = useTranslation('landing')
    const {isLoaded, isSignedIn} = useAuth()
    const staffAccess = usePartnerStaffAccess({enabled: Boolean(isLoaded && isSignedIn)})
    const signInHref = buildAuthHref('/sign-in', redirectUrl)

    if (!isLoaded || (isSignedIn && staffAccess.isLoading)) {
        return <div className={cn('h-10 motion-safe:animate-pulse motion-reduce:animate-none rounded-full bg-slate-100', compact ? 'w-full' : 'w-36')} aria-hidden="true" />
    }

    if (isSignedIn) {
        return (
            <PublicAuthenticatedActions
                compact={compact}
                isStaff={canAccessAdminDashboard(staffAccess.data)}
            />
        )
    }

    return (
        <div className={cn(compact ? 'grid gap-2' : 'flex items-center gap-3')} aria-label={t('publicEntry.cta.title')}>
            <Link
                href={signInHref}
                className={cn(
                    publicEntryStyles.ctaSecondary,
                    compact ? 'h-10 w-full px-3 text-[14px] md:h-10 md:text-[14px]' : 'h-9 px-4 text-[14px] md:h-10 md:text-[15px]',
                )}
            >
                <User className="h-4 w-4" aria-hidden="true" />
                {t('publicEntry.auth.signIn')}
            </Link>
        </div>
    )
}

function PublicAuthenticatedActions({compact, isStaff}: {compact: boolean; isStaff: boolean}) {
    const {t} = useTranslation('landing')
    const {user} = useUser()
    const {signOut} = useClerk()
    const [accountMenuOpen, setAccountMenuOpen] = useState(false)
    const accountMenuMotion = useRetainedMotionState(accountMenuOpen, 150)
    const isAccountMenuClosing = !accountMenuOpen
    const [signingOut, setSigningOut] = useState(false)
    const accountMenuRef = useRef<HTMLDivElement | null>(null)
    const displayName = user?.fullName?.trim()
        || user?.firstName?.trim()
        || t('publicEntry.auth.user')
    const email = user?.primaryEmailAddress?.emailAddress ?? ''
    const initial = displayName.charAt(0).toLocaleUpperCase()
    const primaryHref = isStaff ? '/admin' : '/home'
    const primaryLabel = isStaff
        ? t('publicEntry.auth.trainerDashboard')
        : t('publicEntry.auth.clientDashboard')

    useEffect(() => {
        if (!accountMenuOpen) return

        const handlePointerDown = (event: PointerEvent) => {
            if (!accountMenuRef.current?.contains(event.target as Node)) {
                setAccountMenuOpen(false)
            }
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                setAccountMenuOpen(false)
            }
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [accountMenuOpen])

    const handleSignOut = () => {
        setSigningOut(true)
        setAccountMenuOpen(false)
        void signOut({redirectUrl: '/'}).catch(() => setSigningOut(false))
    }

    return (
        <div
            className={cn(compact ? 'grid gap-2' : 'flex items-center gap-3')}
            aria-label={t('publicEntry.auth.accountActions')}
        >
            {isStaff ? (
                <a
                    href="/home"
                    className={cn(
                        publicEntryStyles.ctaSecondary,
                        compact
                            ? 'h-10 w-full px-3 text-[14px] md:h-10 md:text-[14px]'
                            : 'h-10 max-w-32 whitespace-normal px-4 text-center text-[14px] leading-[1.05] md:h-10 md:text-[14px]',
                    )}
                >
                    {t('publicEntry.auth.clientDashboard')}
                </a>
            ) : null}
            <a
                href={primaryHref}
                className={cn(
                    publicEntryStyles.ctaPrimary,
                    compact
                        ? 'h-10 w-full px-3 text-[14px] md:h-10 md:text-[14px]'
                        : 'h-10 max-w-36 whitespace-normal px-4 text-center text-[14px] leading-[1.05] md:h-10 md:text-[14px]',
                )}
            >
                {primaryLabel}
            </a>

            <div ref={accountMenuRef} className="relative">
                <button
                    type="button"
                    aria-expanded={accountMenuOpen}
                    aria-haspopup="menu"
                    aria-label={t('publicEntry.auth.accountMenu')}
                    className={cn(
                        'inline-flex h-10 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 text-[14px] font-medium text-[var(--landing-primary-600)] shadow-[0_4px_12px_rgba(15,23,42,0.04)] transition-colors hover:border-[var(--landing-primary-300)]',
                        compact && 'w-full justify-between px-3',
                    )}
                    onClick={() => setAccountMenuOpen((open) => !open)}
                >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(237,240,253,0.88)] text-[12px] font-semibold" aria-hidden="true">
                        {initial || <User className="h-4 w-4" />}
                    </span>
                    {compact ? <span className="min-w-0 flex-1 truncate text-left">{displayName}</span> : null}
                    <ChevronDown className={cn('h-4 w-4 transition-transform duration-normal ease-out motion-reduce:transition-none', accountMenuOpen && 'rotate-180')} aria-hidden="true" />
                </button>

                {accountMenuMotion.shouldRender ? (
                    <div
                        role="menu"
                        aria-label={t('publicEntry.auth.accountMenu')}
                        data-state={accountMenuMotion.motionState}
                        aria-hidden={isAccountMenuClosing || undefined}
                        inert={isAccountMenuClosing || undefined}
                        className={cn(
                            'absolute top-[calc(100%+8px)] z-40 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.14)]',
                            POPOVER_MOTION_CLASS,
                            compact ? 'left-0 origin-top-left' : 'right-0 origin-top-right',
                        )}
                    >
                        <div className="border-b border-slate-100 px-4 py-3">
                            <p className="truncate text-[14px] font-semibold text-[var(--landing-neutral-900)]">{displayName}</p>
                            {email ? <p className="mt-0.5 truncate text-[12px] text-[var(--landing-neutral-500)]">{email}</p> : null}
                        </div>
                        <div className="p-1.5">
                            <button
                                type="button"
                                role="menuitem"
                                disabled={signingOut}
                                onClick={handleSignOut}
                                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-wait disabled:opacity-60"
                            >
                                <LogOut className="h-4 w-4" aria-hidden="true" />
                                {t('publicEntry.auth.signOut')}
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

function PublicNavigation({activePage, className, compact = false, showAboutPage = true, onNavigate}: {activePage: PublicSitePage; className: string; compact?: boolean; showAboutPage?: boolean; onNavigate?: () => void}) {
    const {t} = useTranslation('landing')
    const navItems = PUBLIC_SITE_NAV_ITEMS.filter((item) => {
        if (!showAboutPage && item.page === 'about') return false
        return true
    })

    return (
        <nav className={cn('items-center gap-8 text-base font-normal leading-none text-[var(--landing-neutral-500)]', className)} aria-label={t('publicEntry.nav.label')}>
            {navItems.map((item) => (
                <Link
                    key={item.page}
                    href={item.href}
                    onClick={onNavigate}
                    aria-current={item.page === activePage ? 'page' : undefined}
                    className={cn(
                        item.page === activePage
                            ? compact
                                ? 'bg-[rgba(237,240,253,0.88)] text-[var(--landing-primary-600)]'
                                : 'border-b-2 border-[var(--landing-primary-500)] py-6 text-[var(--landing-primary-600)]'
                            : compact
                                ? 'text-[var(--landing-neutral-700)]'
                                : 'border-b-2 border-transparent py-6',
                        compact && 'inline-flex h-10 items-center rounded-md px-2 text-[14px]',
                        item.page !== activePage && 'transition-colors hover:border-[var(--landing-primary-500)] hover:text-[var(--landing-primary-600)]'
                    )}
                >
                    {t(item.key)}
                </Link>
            ))}
        </nav>
    )
}
